import { IValueInterval } from "@components/conditionalFilterDialog/ConditionalFilterDialog.utils";
import { IGroupListItemDef } from "@components/configurationList";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import { formatDateToDateString } from "@components/inputs/date/utils";
import { ISelectItem, SelectGroups } from "@components/inputs/select/Select.types";
import { IFieldInfoProperties, IGetValueArgs } from "@components/smart/FieldInfo";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportColumnFormatterArgs, IReportRowDef, ISmartReportTableRow } from "@components/smart/smartTable";
import { getNestedValue } from "@odata/Data.utils";
import { AccountActivityTypeCode } from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import { CUSTOM_DATE_RANGE_ID } from "@pages/reports/customFilterComponents/ComposedDateRange.utils";
import { getCompanyCurrency, isNonProfitCompany } from "@utils/CompanyUtils";
import { isNotDefined } from "@utils/general";
import i18next from "i18next";
import React from "react";

import { IAppContext } from "../../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    ConfigListItemBoundType,
    FieldType,
    LabelStatus,
    RowType,
    Sort,
    ValueType
} from "../../../enums";
import { TRecordValue, TValue } from "../../../global.types";
import BindingContext from "../../../odata/BindingContext";
import { ROUTE_ACCOUNT_ANALYSIS } from "../../../routes";
import CurrencyType from "../../../types/Currency";
import { AccountAnalysisProps } from "../accountAnalysis/AccountAnalysisDef";
import {
    columnBorderFormatter,
    getReportDisplayType,
    metaColumnFormatter,
    resetReportDisplayType,
    toggleGroupsByReportDisplayType
} from "../balanceSheet/BalanceSheet";
import { getColumnGroupForColumn, IBalanceSheetCustomData } from "../balanceSheet/BalanceSheet.utils";
import {
    CommonReportProps,
    FiscalYearComparison,
    getByDateDef,
    getComparisonDef,
    getDateRangeDef,
    getSplitIntoPeriodsDef,
    getUnitsDef,
    ReportDisplayType,
    ReportSectionTypeCode
} from "../CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getDateRangeCustomValueDef
} from "../customFilterComponents/ComposedDateRange";
import {
    byDateInFYValidationSchema,
    getLabelDrilldownFilters,
    IGetReportDefVal,
    IReportTableDefinition,
    labelColumnNamePrefix,
    NumberAggregationFunction,
    ReportConfigGroup,
    TGetReportConfigListItemOverride,
    TReportConfigListItemOverride
} from "../Report.utils";
import { ReportId } from "../ReportIds";
import { ReportStorage } from "../ReportStorage";
import { IncomeStatementDelta } from "./IncomeStatement";
import { StyledIndication } from "./IncomeStatement.styles";
import { enhanceRowsWithAccountIds } from "./IncomeStatement.utils";

export const initialSortBy = [{ id: "Line", sort: Sort.Asc }];

const INC_STMNT_SHORT_SHEET_OPEN_LINES: string[] = [];
const INC_STMNT_SHORT_SHEET_OPEN_LINES_FOR_NON_PROFIT: string[] = ["1", "40"];

export const getShortOpenLines = (context: IAppContext) => {
    return isNonProfitCompany(context) ? INC_STMNT_SHORT_SHEET_OPEN_LINES_FOR_NON_PROFIT : INC_STMNT_SHORT_SHEET_OPEN_LINES;
};

export const IncomeStatementProps = {
    incomeStatementStructure: BindingContext.localContext("IncomeStatementStructure")
};

export enum IncomeStatementStructure {
    ByNatureOfExpense = "ByNatureOfExpense",
    ByFunctionOfExpense = "ByFunctionOfExpense"
}

const onAfterTableLoad = (storage: ReportStorage<IBalanceSheetCustomData>) => {
    const reportDisplayType = getReportDisplayType(storage);

    if (reportDisplayType !== ReportDisplayType.None) {
        toggleGroupsByReportDisplayType({
            storage,
            key: reportDisplayType,
            openLines: getShortOpenLines(storage.context)
        });
    }
};

const onGroupToggle = (storage: ReportStorage) => {
    resetReportDisplayType(storage);
};

const rowFormatter = (rowDef: IReportRowDef): Partial<ISmartReportTableRow> => {
    const newRow: Partial<ISmartReportTableRow> = {};

    if (rowDef.Value.ReportSectionTypeCode === ReportSectionTypeCode.Sum) {
        newRow.isHighlighted = true;
        newRow.type = RowType.Aggregation;
        newRow.isBold = true;
    }

    return newRow;
};

export const getDefinition = (context: IAppContext): IReportTableDefinition => {
    const isNonProfit = isNonProfitCompany(context);
    const tableId = ReportId.IncomeStatement;
    const title = i18next.t("Reporting:IncomeStatement.Title");
    const path = !isNonProfit ? "IncomeStatement" : "IncomeStatementNonprofit";
    const nonProfitParameters = [
        CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue,
        CommonReportProps.byDate,
        CommonReportProps.units
    ];
    const parameters: string[] = [
        IncomeStatementProps.incomeStatementStructure,
        CommonReportProps.dateRange,
        CommonReportProps.byDate,
        CommonReportProps.dateRangeCustomValue,
        CommonReportProps.splitIntoPeriods,
        CommonReportProps.showDelta,
        CommonReportProps.comparison
    ].filter(val => !isNonProfit ? true : nonProfitParameters.includes(val));

    const updateFiltersFromResponse = true;

    const configListItemsOverride: TGetReportConfigListItemOverride = (item: IGroupListItemDef): TReportConfigListItemOverride => {
        switch (true) {
            case item.id === "Section":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    group: ReportConfigGroup.Groups
                };
            case item.id === "Line":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    boundTo: ConfigListItemBoundType.Group,
                    group: ReportConfigGroup.Columns

                };
            case item.id === "Number":
                return {
                    ...item,
                    isRequired: true,
                    isCopyOnly: false,
                    boundTo: ConfigListItemBoundType.Group,
                    group: ReportConfigGroup.Columns
                };
            case item.id === "Indication":
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    boundTo: ConfigListItemBoundType.Group,
                    group: ReportConfigGroup.Columns
                };
            case ["JournalEntry_Amount", "JournalEntry_Main", "JournalEntry_Business"].includes(item.id):
                return {
                    ...item,
                    isRequired: true,
                    isDisabled: true,
                    isCopyOnly: false,
                    areItemsReadOnly: true,
                    value: i18next.t(`Reporting:IncomeStatement.Values`) as string,
                    group: ReportConfigGroup.Aggregations
                };
            case item.id.startsWith(labelColumnNamePrefix):
                return {
                    ...item,
                    allowedGroups: [ReportConfigGroup.Groups, ReportConfigGroup.Columns, ReportConfigGroup.AvailableColumns]
                };
        }

        return item;
    };

    const defaultReportHierarchy = {
        "Aggregate": true,
        "Groups": [
            {
                "ColumnAlias": "Section"
            }
        ],
        "Columns": [
            {
                "ColumnAlias": "Indication"
            },
            {
                "ColumnAlias": "Line"
            }
        ],
        "Aggregations": [
            {
                "ColumnAlias": "JournalEntry_Amount",
                "AggregationFunction": NumberAggregationFunction.Sum
            }
        ]

    };

    if (isNonProfit) {
        defaultReportHierarchy.Aggregations.push(
            {
                "ColumnAlias": "JournalEntry_Main",
                "AggregationFunction": NumberAggregationFunction.Sum
            },
            {
                "ColumnAlias": "JournalEntry_Business",
                "AggregationFunction": NumberAggregationFunction.Sum
            });
    }

    /** Backend sends account ids that are needed for drilldown only for the leaves
     * => we need to collect those data and propagate them to all levels */
    const rowsDataFactory = (rowsData: IReportRowDef[]): IReportRowDef[] => {
        enhanceRowsWithAccountIds(rowsData, ["AccountNumbers"]);
        // clear cache for getColumnGroupForColumn, in case column ids has changed
        getColumnGroupForColumn.cache.clear();

        return rowsData;
    };

    const columnOverrides = (columnId: string): IFieldInfoProperties => {
        const formatCurrency = (val: number, storage: ReportStorage) => {
            return CurrencyType.format(val, {
                currency: getCompanyCurrency(storage.context),
                scale: currencyUnit({ storage: storage, settings: {} })
            });
        };

        if (columnId === "Indication") {
            return {
                formatter: (val: TValue, args: IFormatOptions) => {
                    // we display sum for nonprofit
                    if (isNotDefined(val) || (args.entity.ReportSectionTypeCode === ReportSectionTypeCode.Sum && !isNonProfit)) {
                        return null;
                    }

                    const isIncome = args.entity.ReportSectionTypeCode === ReportSectionTypeCode.Income;

                    return {
                        value: (<StyledIndication isIncome={isIncome}>{val}</StyledIndication>),
                        tooltip: val as string
                    };
                }
            };
        } else if (columnId.startsWith("Value")) {
            return {
                formatter: (val: TValue, args: IFormatOptions) => {
                    const storage = args.storage as ReportStorage;
                    const formattedVal = formatCurrency(val as number, storage);

                    // we hide values for both two main sections for non profit
                    if (isNonProfit && !val && INC_STMNT_SHORT_SHEET_OPEN_LINES_FOR_NON_PROFIT.includes(args.entity.Line?.toString?.())) {
                        return null;
                    }

                    if (!val || val === 0 || args.entity.ReportSectionTypeCode === ReportSectionTypeCode.Sum) {
                        return formattedVal;
                    }

                    const isComp = columnId.includes("Comp");
                    const accountsPropName = "AccountNumbers" + (isComp ? "_Comp" : "");
                    const accounts = args.entity[accountsPropName];

                    const filters: TRecordValue = {
                        [CommonReportProps.dateRange]: CUSTOM_DATE_RANGE_ID,
                        [AccountAnalysisProps.accounts]: accounts,
                        [AccountAnalysisProps.sumAmounts]: true,
                        ...getLabelDrilldownFilters(args.entity)
                    };

                    if (isNonProfit && (columnId === "Value_Main" || columnId === "Value_Business")) {
                        filters[BindingContext.localContext("AccountActivityType_Name")] = columnId === "Value_Main" ? AccountActivityTypeCode.Main : AccountActivityTypeCode.Business;
                    }

                    const columnGroup = getColumnGroupForColumn(columnId, args.storage as ReportStorage);
                    const dateStart: string = columnGroup.AdditionalInfo.FirstDayOfPeriod;
                    const dateEnd: string = columnGroup.AdditionalInfo.LastDayOfPeriod;

                    const dateRangeParam: IValueInterval = {
                        from: formatDateToDateString(dateStart),
                        to: formatDateToDateString(dateEnd)
                    };

                    filters[CommonReportProps.dateRangeCustomValue] = dateRangeParam;

                    return getTableDrillDownLink(formattedVal, {
                        route: ROUTE_ACCOUNT_ANALYSIS,
                        context: args.storage.context,
                        storage: args.storage,
                        filters
                    });
                }
            };
        }

        return null;
    };

    const currencyUnit = (args: IGetReportDefVal) => {
        return getNestedValue(CommonReportProps.units, args.storage.data.entity);
    };

    const dateRangeDef = getDateRangeDef();

    const filterBarDef: IFilterGroupDef[] = [
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
            defaultFilters: [
                IncomeStatementProps.incomeStatementStructure,
                CommonReportProps.dateRange,
                CommonReportProps.byDate,
                CommonReportProps.dateRangeCustomValue,
                CommonReportProps.comparison,
                CommonReportProps.showDelta,
                CommonReportProps.splitIntoPeriods,
                CommonReportProps.units
            ].filter(val => !isNonProfit ? true : nonProfitParameters.includes(val)),
            filterDefinition: {
                [IncomeStatementProps.incomeStatementStructure]: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.M,
                    defaultValue: IncomeStatementStructure.ByNatureOfExpense,
                    label: i18next.t("Reporting:IncomeStatement.IncomeStatementStructure"),
                    fieldSettings: {
                        items: [
                            {
                                label: i18next.t("Reporting:IncomeStatement.ByNatureOfExpense"),
                                id: IncomeStatementStructure.ByNatureOfExpense
                            },
                            {
                                label: i18next.t("Reporting:IncomeStatement.ByFunctionOfExpense"),
                                id: IncomeStatementStructure.ByFunctionOfExpense
                            }
                        ]
                    }
                },
                [CommonReportProps.byDate]: {
                    ...getByDateDef(),
                    defaultValue: null,
                    validator: {
                        ...getByDateDef().validator,
                        settings: {
                            customSchema: byDateInFYValidationSchema
                        }
                    },
                    filter: {
                        customReadOnlyParsedValue: (args: IGetValueArgs) => {
                            return args.storage.data.entity[CommonReportProps.byDate];
                        }
                    }
                },
                [CommonReportProps.dateRange]: {
                    ...dateRangeDef,
                    fieldSettings: {
                        ...dateRangeDef.fieldSettings,
                        transformFetchedItems: (items: ISelectItem[]): ISelectItem[] => {
                            return items.filter(item => !item.id.toString().includes("FiscalPeriod"));
                        }
                    }
                },
                [CommonReportProps.dateRangeCustomValue]: getDateRangeCustomValueDef(),
                [CommonReportProps.comparison]: getComparisonDef(false, true),
                [CommonReportProps.showDelta]: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.M,
                    defaultValue: "No",
                    label: i18next.t("Reporting:BalanceSheet.ShowDelta"),
                    labelStatus: LabelStatus.Visible,
                    isDisabled: (arg) => {
                        const comparison = arg.storage.getValueByPath(CommonReportProps.comparison);

                        return !comparison || comparison === FiscalYearComparison.None;
                    },
                    fieldSettings: {
                        items: [
                            {
                                label: i18next.t("Reporting:IncomeStatement.InValues"),
                                id: IncomeStatementDelta.Yes
                            },
                            {
                                label: i18next.t("Reporting:IncomeStatement.InPercent"),
                                id: IncomeStatementDelta.InPercent
                            }
                        ],
                        additionalItems: [
                            {
                                label: i18next.t("Reporting:IncomeStatement.DontShow"),
                                id: IncomeStatementDelta.No,
                                groupId: SelectGroups.Default
                            }
                        ]
                    }
                },
                [CommonReportProps.splitIntoPeriods]: getSplitIntoPeriodsDef(),
                [CommonReportProps.units]: getUnitsDef()
            }
        },
        {
            ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
            allowCustomFilters: false,
            // filters that are not dynamic and can be specified before hand
            // rest are created AFTER the data are loaded from BE
            defaultFilters: [
                BindingContext.localContext("Section"),
                BindingContext.localContext("Indication"),
                BindingContext.localContext("Line")
            ],
            filterDefinition: {
                [BindingContext.localContext("Section")]: {
                    label: i18next.t("Reporting:Columns.Section"),
                    valueType: ValueType.String,
                    isValueHelp: true
                },
                [BindingContext.localContext("Indication")]: {
                    label: i18next.t("Reporting:Columns.Indication"),
                    valueType: ValueType.String,
                    isValueHelp: true
                },
                [BindingContext.localContext("Line")]: {
                    label: i18next.t("Reporting:Columns.Line"),
                    valueType: ValueType.String,
                    isValueHelp: true
                }
            }
        }
    ];

    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, false, false);
    };

    const columnFormatter = (args: IReportColumnFormatterArgs) => {
        if (args.column.id !== "Value_Main") {
            return columnBorderFormatter(args);
        }

        return { ...args.column };
    };

    return {
        title, path, id: tableId,
        columnOverrides, onAfterTableLoad,
        onBeforeLoad, rowsDataFactory, currencyUnit,
        onGroupToggle, rowFormatter, initialSortBy,
        metaColumnFormatter, updateFiltersFromResponse,
        columnFormatter: columnFormatter, filterBarDef, parameters,
        defaultReportHierarchy,
        configListItemsOverride
    };
};